// Staff-friendly wording for Digital Waste Tracking states. Our own words only:
// Defra's text is never shown. Keep these in step with server
// submissions/submissionRules.ts (SubmissionStatus) and deadlines.ts (DueState).

import type { BadgeVariant } from '../../components/ui/Badge/Badge';
import type { DwtDueState } from '../../api/dwt';

export const DWT_STATUS_LABEL: Record<string, string> = {
  none: 'Not reported',
  queued: 'Queued for Defra',
  pending: 'Sending to Defra…',
  submitted: 'Reported to Defra',
  failed_retryable: 'Will retry',
  failed_permanent: 'Failed: needs fixing',
  blocked_missing_data: 'Missing information',
  unknown_outcome: 'Check with Defra',
  service_charge_unpaid: 'Paused: service charge unpaid',
  amendment_required: 'Amendment required',
  superseded: 'Replaced by an amendment',
};

export const DWT_STATUS_VARIANT: Record<string, BadgeVariant> = {
  none: 'muted',
  queued: 'info',
  pending: 'info',
  submitted: 'success',
  failed_retryable: 'warning',
  failed_permanent: 'danger',
  blocked_missing_data: 'warning',
  unknown_outcome: 'danger',
  service_charge_unpaid: 'warning',
  amendment_required: 'warning',
  superseded: 'muted',
};

/** A one-line "what this means / what to do" for each state. */
export const DWT_STATUS_HELP: Record<string, string> = {
  none: 'This receipt has not been sent to Defra.',
  queued: 'Waiting to be sent to Defra.',
  pending: 'A request to Defra is in progress.',
  submitted: 'Defra has accepted this receipt.',
  failed_retryable: 'Defra could not be reached or was busy. It will be tried again automatically.',
  failed_permanent: 'Defra did not accept this receipt. Fix the problems shown, then queue it again.',
  blocked_missing_data: 'Some information needed by Defra is missing. Add it, then queue the receipt again.',
  unknown_outcome:
    'We could not confirm whether Defra received it. It is not sent again until an admin checks Defra’s service and resolves it.',
  service_charge_unpaid: 'Defra says the service charge is unpaid, so reporting is paused for the company.',
  amendment_required: 'The receipt changed after it was reported. Send an amendment so Defra has the new details.',
  superseded: 'An earlier report that has since been replaced by an amendment.',
};

export const DWT_DUE_LABEL: Record<DwtDueState, string> = {
  ok: 'On time',
  due_within_24h: 'Due within 24 hours',
  overdue: 'Overdue',
};

export const DWT_DUE_VARIANT: Record<DwtDueState, BadgeVariant> = {
  ok: 'success',
  due_within_24h: 'warning',
  overdue: 'danger',
};

export function dwtStatusLabel(status: string | null | undefined): string {
  return DWT_STATUS_LABEL[status ?? 'none'] ?? 'Unknown';
}

/** Plain wording for the receipt’s consignment information. */
export const NO_CONSIGNMENT_REASON_LABEL: Record<string, string> = {
  NON_HAZ_WASTE_TRANSFER: 'Non-hazardous waste transfer (no consignment note needed)',
  NO_DOC_WITH_WASTE: 'No documents came with the waste',
  HWRC_RECEIPT: 'Received at a household waste recycling centre',
};

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/London',
  });
}

/** "2 days 3 hours left" / "5 hours overdue". `now` is injectable for tests. */
export function countdownText(dueIso: string | null | undefined, now: Date = new Date()): string {
  if (!dueIso) return '';
  const due = new Date(dueIso).getTime();
  if (Number.isNaN(due)) return '';
  const ms = due - now.getTime();
  const abs = Math.abs(ms);
  const mins = Math.floor(abs / 60_000);
  const days = Math.floor(mins / 1440);
  const hours = Math.floor((mins % 1440) / 60);
  const m = mins % 60;
  const parts: string[] = [];
  if (days > 0) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
  if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
  if (days === 0 && hours === 0) parts.push(`${m} ${m === 1 ? 'minute' : 'minutes'}`);
  const text = parts.join(' ');
  return ms < 0 ? `${text} overdue` : `${text} left`;
}
